import { useSelector } from "react-redux";
import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import React from "react";
import { Chip, Box, Typography, alpha, LinearProgress } from "@mui/material";
import {
  Home,
  PowerSettingsNew,
  PowerOff,
  WaterDrop,
  ElectricBolt,
  SentimentSatisfiedAlt,
  Circle,
} from "@mui/icons-material";

// Fonction pour obtenir la couleur selon la valeur (green, yellow, red)
const getStatusColor = (color) => {
  const c = color?.toLowerCase() || "";
  if (c === "green" || c === "vert") return "#78a882";
  if (c === "yellow" || c === "jaune") return "#c4a574";
  if (c === "red" || c === "rouge") return "#a57474";
  return "#7a8a7a";
};

export default function ScreensTable({ filteredScreens }) {
  const allScreens = useSelector((state) => state.screens);
  const residences = useSelector((state) => state.residences);

  // Utiliser les écrans filtrés si fournis, sinon tous les écrans
  const screens = filteredScreens || allScreens;

  const { t } = useTranslation("common");

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: "pack_id",
      label: t("common.residence"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const residence = residences.find((res) => res.pack_id === value);
          if (residence) {
            return (
              <Chip
                icon={<Home sx={{ fontSize: 16 }} />}
                label={residence.name}
                size="small"
                sx={{
                  backgroundColor: alpha("#6b9dad", 0.15),
                  color: "#ffffff",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "#6b9dad" },
                }}
              />
            );
          }
          return (
            <Typography variant="body2" sx={{ color: "#7a8a7a" }}>
              —
            </Typography>
          );
        },
      },
    },
    {
      name: "screen_on",
      label: t("common.screen_on"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (value) {
            return (
              <Chip
                icon={<PowerSettingsNew sx={{ fontSize: 16 }} />}
                label={t("common.yes")}
                size="small"
                sx={{
                  backgroundColor: alpha("#78a882", 0.15),
                  color: "#ffffff",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "#78a882" },
                }}
              />
            );
          }
          return (
            <Chip
              icon={<PowerOff sx={{ fontSize: 16 }} />}
              label={t("common.no")}
              size="small"
              variant="outlined"
              sx={{
                borderColor: alpha("#7a8a7a", 0.4),
                color: "#7a8a7a",
                "& .MuiChip-icon": { color: "#7a8a7a" },
              }}
            />
          );
        },
      },
    },
    {
      name: "smiley_color",
      label: t("common.smiley_color"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const color = getStatusColor(value);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SentimentSatisfiedAlt sx={{ color: color, fontSize: 20 }} />
              <Circle sx={{ color: color, fontSize: 12 }} />
            </Box>
          );
        },
      },
    },
    {
      name: "water_liter",
      label: t("common.water_liter"),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <WaterDrop sx={{ color: "#6b9dad", fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value || 0} L
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: "electricity_kwh",
      label: t("common.electricity_kwh"),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <ElectricBolt sx={{ color: "#c4a574", fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value || 0} kWh
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: "water_color",
      label: t("common.water_color"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const color = getStatusColor(value);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WaterDrop sx={{ color: color, fontSize: 18 }} />
              <Circle sx={{ color: color, fontSize: 10 }} />
            </Box>
          );
        },
      },
    },
    {
      name: "electricity_color",
      label: t("common.electricity_color"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const color = getStatusColor(value);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ElectricBolt sx={{ color: color, fontSize: 18 }} />
              <Circle sx={{ color: color, fontSize: 10 }} />
            </Box>
          );
        },
      },
    },
    {
      name: "screen_brightness",
      label: t("common.screen_brightness"),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          const brightness = value || 0;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 100 }}>
              <LinearProgress
                variant="determinate"
                value={brightness}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: alpha("#7a8a7a", 0.2),
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#8a9ba8",
                    borderRadius: 3,
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: "#9aa89a", minWidth: 35 }}>
                {brightness}%
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  const tableActions = [];

  return (
    <DataTableBase
      columns={columns}
      data={screens}
      title={t("common.screens")}
      id="screen-list"
      name="screen-list"
      pageSize={10}
      actions={tableActions}
      sortOrder="pack_id"
      onRowClick={(rowData, rowMeta) => {
        // Screen row click handler
      }}
    />
  );
}
